import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/authStore';
import  api  from '../../services/api';

interface GovAgency {
  id: string;
  name: string;
  email: string;
  website: string;
  agencyType: string;
  contactPerson: string;
  approved: boolean;
  domainVerified: boolean;
  govDocs: string[];
  createdAt: string;
  updatedAt: string;
  emailDomain: string;
  status: 'pending' | 'approved';
  users: Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    isAgency: boolean;
    createdAt: string;
  }>;
}

interface AgencyStats {
  total: number;
  pending: number;
  approved: number;
}

// Domain classification types
type DomainTrustLevel = 'trusted' | 'caution' | 'warning';

interface DomainClassification {
  level: DomainTrustLevel;
  label: string;
  icon: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
}

const GovAgenciesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [agencies, setAgencies] = useState<GovAgency[]>([]);
  const [stats, setStats] = useState<AgencyStats>({ total: 0, pending: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedAgency, setSelectedAgency] = useState<GovAgency | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [deleteAgency, setDeleteAgency] = useState(false);
  // 1. Set default filter to 'pending'
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved'>('pending');
  // Add state for action loading
  // const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Email domain classification function
  const classifyEmailDomain = (email: string): DomainClassification => {
    const domain = email.toLowerCase().split('@')[1];
    
    // Trusted government domains
    if (domain.endsWith('.gov') || 
        domain.endsWith('.mil') || 
        domain.endsWith('.edu') ||
        domain.endsWith('.us') ||
        domain.endsWith('.state.') ||
        domain.endsWith('.city.') ||
        domain.endsWith('.county.') ||
        domain.endsWith('.k12.') ||
        domain.endsWith('.cc.us')) {
      return {
        level: 'trusted',
        label: 'Trusted Gov Domain',
        icon: '✅',
        bgColor: 'bg-green-50',
        textColor: 'text-green-800',
        borderColor: 'border-green-200'
      };
    }
    
    // Caution domains (organizations, could be legitimate)
    if (domain.endsWith('.org')) {
      return {
        level: 'caution',
        label: 'Organization Domain',
        icon: '⚠️',
        bgColor: 'bg-yellow-50',
        textColor: 'text-yellow-800',
        borderColor: 'border-yellow-200'
      };
    }
    
    // Warning domains (commercial, requires verification)
    return {
      level: 'warning',
      label: 'Requires Verification',
      icon: '❌',
      bgColor: 'bg-red-50',
      textColor: 'text-red-800',
      borderColor: 'border-red-200'
    };
  };

  // Get domain status badge
  const getDomainStatusBadge = (email: string) => {
    const classification = classifyEmailDomain(email);
    
    return (
      <div className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${classification.bgColor} ${classification.textColor} ${classification.borderColor}`}>
        <span className="mr-1">{classification.icon}</span>
        {classification.label}
      </div>
    );
  };

  // Get domain warning tooltip
  const getDomainWarning = (email: string): string | null => {
    const classification = classifyEmailDomain(email);
    
    switch (classification.level) {
      case 'trusted':
        return 'This is a verified government domain. Safe to approve.';
      case 'caution':
        return 'Organization domain - verify this is a legitimate government organization before approving.';
      case 'warning':
        return 'Commercial domain - requires additional verification. Ensure this agency has proper government authorization.';
      default:
        return null;
    }
  };

  // Check admin access
  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/login');
      return;
    }
  }, [user, navigate]);

  // Fetch agencies
  const fetchAgencies = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get<{ success: boolean; data: any[]; message?: string }>('/api/admin/gov-agencies');
      if (response.success) {
        setAgencies(response.data);
        // Calculate stats from the array
        setStats({
          total: response.data.length,
          pending: response.data.filter(a => !a.approved).length,
          approved: response.data.filter(a => a.approved).length
        });
      } else {
        throw new Error(response.message || 'Failed to fetch agencies');
      }
    } catch (err: any) {
      console.error('Error fetching agencies:', err);
      setError(err?.response?.data?.message || err?.message || 'Failed to fetch government agencies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgencies();
  }, []);

  // Approve handler
  const handleApprove = async (agencyId: string) => {
    // setActionLoading(agencyId + '-approve');
    setError(null);
    try {
      await api.post(`/api/admin/gov-agencies/${agencyId}/approve`);
      await fetchAgencies();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || 'Failed to approve agency');
    } finally {
      // setActionLoading(null);
    }
  };

  // Reject handler
  const handleReject = async () => {
    if (!selectedAgency) return;
    
    try {
      setProcessingId(selectedAgency.id);
      
      const response = await api.post(`/admin/gov-agencies/${selectedAgency.id}/reject`, {
        reason: rejectReason,
        deleteAgency: deleteAgency
      })as any;
      
      if (response.data.success) {
        await fetchAgencies(); // Refresh the list
        setShowRejectModal(false);
        setRejectReason('');
        setDeleteAgency(false);
        setSelectedAgency(null);
        alert(`Agency ${deleteAgency ? 'deleted' : 'rejected'} successfully!`);
      } else {
        throw new Error(response.data.message || 'Failed to reject agency');
      }
    } catch (err: any) {
      console.error('Error rejecting agency:', err);
      alert(err.response?.data?.message || 'Failed to reject agency');
    } finally {
      setProcessingId(null);
    }
  };

  // Open reject modal
  const openRejectModal = (agency: GovAgency) => {
    setSelectedAgency(agency);
    setShowRejectModal(true);
  };

  // Close reject modal
  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason('');
    setDeleteAgency(false);
    setSelectedAgency(null);
  };

  // 2. Filter agencies based on filter state
  const filteredAgencies = agencies.filter((agency) => {
    if (filter === 'all') return true;
    if (filter === 'pending') return agency.approved === false;
    if (filter === 'approved') return agency.approved === true;
    return true;
  });

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (agency: GovAgency) => {
    if (agency.approved) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✅ Approved
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        ⏳ Pending
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Government Agency Management</h1>
          <p className="mt-2 text-gray-600">Review and manage government agency registrations</p>
          
          {/* Domain Classification Legend */}
          <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Email Domain Classification:</h3>
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center">
                <span className="mr-2">✅</span>
                <span className="text-green-700">Trusted Gov Domains (.gov, .mil, .edu)</span>
              </div>
              <div className="flex items-center">
                <span className="mr-2">⚠️</span>
                <span className="text-yellow-700">Organization Domains (.org) - Verify legitimacy</span>
              </div>
              <div className="flex items-center">
                <span className="mr-2">❌</span>
                <span className="text-red-700">Commercial Domains (.com, .net) - Requires verification</span>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-semibold">📊</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Agencies</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                  <span className="text-yellow-600 font-semibold">⏳</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Pending Review</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.pending}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-green-600 font-semibold">✅</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Approved</p>
                <p className="text-2xl font-semibold text-gray-900">{stats.approved}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="mb-4 flex gap-2">
          <button
            className={`px-3 py-1 rounded ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`px-3 py-1 rounded ${filter === 'pending' ? 'bg-yellow-500 text-white' : 'bg-gray-200'}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`px-3 py-1 rounded ${filter === 'approved' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <span className="text-red-400">❌</span>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Agencies Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Agency
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email Domain Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Documents
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Registered
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredAgencies.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-gray-500">
                      No agencies found for this filter.
                    </td>
                  </tr>
                ) : (
                  filteredAgencies.map((agency) => {
                    console.log('Agency row:', agency); // <-- Add this
                    const domainWarning = getDomainWarning(agency.email);
                    const classification = classifyEmailDomain(agency.email);
                    
                    return (
                      <tr key={agency.id} className={`hover:bg-gray-50 ${classification.level === 'warning' ? 'bg-red-25' : classification.level === 'caution' ? 'bg-yellow-25' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{agency.name}</div>
                            <div className="text-sm text-gray-500">{agency.agencyType}</div>
                            <div className="text-sm text-gray-500">{agency.website}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{agency.contactPerson}</div>
                            <div className="text-sm text-gray-500">{agency.email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="space-y-2">
                            {getDomainStatusBadge(agency.email)}
                            <div className="text-xs text-gray-600">{agency.emailDomain}</div>
                            {domainWarning && (
                              <div className={`text-xs p-2 rounded ${classification.bgColor} ${classification.textColor} border ${classification.borderColor}`}>
                                💡 {domainWarning}
                              </div>
                            )}
                            {agency.domainVerified && (
                              <div className="text-xs text-green-600 flex items-center">
                                <span className="mr-1">✓</span>
                                Domain Verified
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {agency.govDocs.length > 0 ? (
                              <div>
                                <span className="text-blue-600">{agency.govDocs.length} file(s)</span>
                                <div className="text-xs text-gray-500">
                                  {agency.govDocs.map((doc, index) => (
                                    <a
                                      key={index}
                                      href={`/uploads/govDocs/${doc}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block hover:text-blue-600"
                                    >
                                      📄 {doc}
                                    </a>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="text-gray-400">No documents</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(agency)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(agency.createdAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          {/* Always render for pending agencies */}
                          {agency.approved === false ? (
                            <>
                              {/* <span style={{color: 'red', fontWeight: 'bold'}}>DEBUG: ACTIONS CELL</span> */}
                              <button
                                className="bg-green-600 text-white px-3 py-1 rounded mr-2 disabled:opacity-50"
                                onClick={() => handleApprove(agency.id)}
                              >
                                Approve
                              </button>
                              <button
                                className="bg-red-600 text-white px-3 py-1 rounded disabled:opacity-50"
                                onClick={() => openRejectModal(agency)}
                              >
                                Reject
                              </button>
                            </>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* This block is now redundant as filteredAgencies.length === 0 handles the message */}
          {/* {filteredAgencies.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500">
                {filter === 'all' ? 'No agencies found' : `No ${filter} agencies found`}
              </div>
            </div>
          )} */}
        </div>

        {/* Reject Modal */}
        {showRejectModal && selectedAgency && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Reject Agency: {selectedAgency.name}
                </h3>
                
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason for rejection:
                  </label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter reason for rejection..."
                  />
                </div>

                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={deleteAgency}
                      onChange={(e) => setDeleteAgency(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      Delete agency record (cannot be undone)
                    </span>
                  </label>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={closeRejectModal}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReject}
                    disabled={processingId === selectedAgency.id}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {processingId === selectedAgency.id ? 'Processing...' : 'Reject Agency'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GovAgenciesPage; 